import React from "react";
import { createRoot } from "react-dom/client";
import { SegmentedChoice } from "react-segmented-choice";

/** Raycast dark: neutral fills, no colour (Pass faint, Partial stronger, Fail white). */
const PASS_FILL = "rgba(255, 255, 255, 0.08)";
const PARTIAL_FILL = "rgba(255, 255, 255, 0.18)";
const FAIL_FILL = "#ffffff";

const QC_OPTIONS = [
  { value: "pass", label: "Pass", accentColor: PASS_FILL },
  { value: "partial", label: "Partial", accentColor: PARTIAL_FILL },
  { value: "fail", label: "Fail", accentColor: FAIL_FILL },
];

/**
 * R13 SegmentedChoice for Pass|Partial|Fail.
 * Single-select (radio); clicking active does not clear.
 * v1.3.22: the thumb is positioned in CSS percentages keyed off the
 * qc-seg--{pass,partial,fail} class (see styles.css), not measured pixels,
 * so it follows resizes; the library's px measurement is overridden.
 */
/** Same-button double-click guard (ms): a fast second click neither re-selects nor clears. */
const SAME_BUTTON_GUARD_MS = 350;
let lastClick = { value: null, at: 0 };
let down = { value: null, wasSelected: false };

function guarded(optValue) {
  const now = Date.now();
  const hit = lastClick.value === optValue && now - lastClick.at < SAME_BUTTON_GUARD_MS;
  lastClick = { value: optValue, at: now };
  return hit;
}

function optionValueOf(target) {
  const opt = target && target.closest && target.closest(".rsc-option");
  const input = opt && opt.querySelector("input.rsc-option-input");
  return input ? input.value : null;
}

function QcSegmentInner({ value, disabled, onChange, labelledBy, labels }) {
  // v1.3.24: pending/unknown is a real empty state (null), not "fail". The library treats a
  // controlled value that matches no option as "nothing selected" (no checked radio, index -1).
  const safe = value === "pass" || value === "partial" || value === "fail" ? value : null;
  const L = labels || {};
  const options = [
    { value: "pass", label: L.pass || "Pass", accentColor: PASS_FILL },
    { value: "partial", label: L.partial || "Partial", accentColor: PARTIAL_FILL },
    { value: "fail", label: L.fail || "Fail", accentColor: FAIL_FILL },
  ];

  // v1.3.25: clicking the selected button again undoes it back to pending. The library ignores
  // re-selecting the current value and captures the pointer (click is retargeted to the list),
  // so the undo is decided on pointerup by hit-testing the option under the pointer.
  const optionAt = (e) => {
    const els = document.elementsFromPoint ? document.elementsFromPoint(e.clientX, e.clientY) : [];
    const opt = els.find((el) => el.classList && el.classList.contains("rsc-option"));
    return opt ? optionValueOf(opt) : null;
  };
  const onPointerDownCapture = (e) => {
    if (e.button !== 0) return;
    const v = optionAt(e) || optionValueOf(e.target);
    down = { value: v, wasSelected: !!v && v === safe };
  };
  const onPointerUpCapture = (e) => {
    if (disabled || e.button !== 0) return;
    const was = down;
    down = { value: null, wasSelected: false };
    if (!was.wasSelected || !was.value || optionAt(e) !== was.value) return;
    if (guarded(was.value)) return;
    onChange?.("pending");
  };

  return (
    <div style={{ display: "contents" }} onPointerDownCapture={onPointerDownCapture} onPointerUpCapture={onPointerUpCapture}>
    <SegmentedChoice
      className={`qc-seg qc-seg--${safe || "none"}`}
      name="aigc-qc-status"
      ariaLabelledby={labelledBy}
      ariaLabel={labelledBy ? undefined : "QC 状态"}
      optionSizing="equal"
      size="md"
      value={safe}
      disabled={!!disabled}
      options={options}
      onValueChange={(next) => {
        if (!next || next === safe) return;
        if (guarded(next)) return;
        onChange?.(next);
      }}
      geometry={{
        mode: "underlay",
        track: { layout: "container", style: "surface" },
        indicator: { style: "fill", transition: "smooth", inset: 3 },
      }}
    />
    </div>
  );
}

let root = null;
let hostEl = null;

/**
 * Mount or re-render R13 into a host element (vanilla app island).
 * @param {HTMLElement} el
 * @param {{ value: string, disabled?: boolean, onChange: (v: string) => void, labelledBy?: string }} props
 */
export function mountQcSegment(el, props) {
  if (!el) return;
  if (hostEl !== el) {
    if (root) {
      try {
        root.unmount();
      } catch (_) {}
      root = null;
    }
    hostEl = el;
    root = createRoot(el);
  }
  root.render(
    <QcSegmentInner
      value={props.value}
      disabled={props.disabled}
      onChange={props.onChange}
      labelledBy={props.labelledBy}
      labels={props.labels}
    />
  );
}

export function unmountQcSegment() {
  if (root) {
    try {
      root.unmount();
    } catch (_) {}
    root = null;
    hostEl = null;
  }
}

export { QcSegmentInner };
